/**
 * Numéros de téléphone au format E.164 — « +237699000111 ».
 *
 * ⭐ Pourquoi normaliser plutôt qu'envoyer tel quel : WhatsApp n'accepte que
 * l'international. Un carnet d'adresses réel contient « 06 99 00 01 11 »,
 * « 699-000-111 », « 00237699000111 » et « +237 699 00 01 11 » pour la même
 * personne. Sans forme canonique, la même cliente est comptée quatre fois,
 * reçoit le message quatre fois, et le signale une fois.
 *
 * ⚠️ Ce module ne VALIDE pas l'existence d'un numéro, il le met en forme. Seul
 * l'opérateur sait si la ligne existe ; c'est l'envoi qui le dira.
 */
/**
 * Indicatifs des pays où IOBIZ est déployé ou attendu.
 *
 * ⚠️ Liste volontairement COURTE. Une table mondiale donnerait l'illusion
 * d'une couverture qu'on ne teste pas : un pays absent d'ici fait tomber le
 * numéro dans la règle « déjà international ou rien », ce qui échoue
 * visiblement plutôt que d'inventer un préfixe.
 */
export const INDICATIFS = {
    CM: '237', CI: '225', SN: '221', ML: '223', BF: '226', NE: '227',
    TG: '228', BJ: '229', GA: '241', CG: '242', CD: '243', TD: '235',
    GN: '224', MR: '222', MA: '212', DZ: '213', TN: '216', HT: '509',
    FR: '33', BE: '32', CH: '41', LU: '352', CA: '1', US: '1',
};
/**
 * Met un numéro en forme internationale.
 *
 * Renvoie `null` quand il n'y a rien d'exploitable — un champ vide, trois
 * chiffres, une note « voir avec sa sœur ». Un `null` explicite vaut mieux
 * qu'un numéro tronqué : le destinataire sera écarté et le rapport le dira.
 */
export function versE164(brut, pays) {
    if (!brut)
        return null;
    // Tout ce qui n'est ni chiffre ni « + » disparaît : espaces, points, tirets,
    // parenthèses, et le « (dom) » que les gens ajoutent au bout.
    const nettoye = brut.replace(/[^\d+]/g, '');
    if (!nettoye)
        return null;
    // Déjà international : on le croit sur parole, c'est la forme demandée.
    if (nettoye.startsWith('+')) {
        const chiffres = nettoye.slice(1);
        return chiffres.length >= 8 && chiffres.length <= 15 ? `+${chiffres}` : null;
    }
    // « 00 » est le « + » composé au clavier d'un téléphone fixe.
    if (nettoye.startsWith('00')) {
        const chiffres = nettoye.slice(2);
        return chiffres.length >= 8 && chiffres.length <= 15 ? `+${chiffres}` : null;
    }
    const indicatif = pays ? INDICATIFS[pays.toUpperCase()] : undefined;
    if (!indicatif)
        return null;
    // Le zéro de tête est national — il ne se compose pas depuis l'étranger.
    // ⚠️ Uniquement pour les plans qui en ont un : au Cameroun, « 0 » n'est pas
    // un préfixe de trafic, et le retirer amputerait un numéro valide. Les plans
    // à zéro de tête concernés ici sont européens.
    const aZeroDeTete = ['33', '32', '41', '352', '212', '213', '216'].includes(indicatif);
    const local = aZeroDeTete ? nettoye.replace(/^0+/, '') : nettoye;
    if (local.length < 6)
        return null;
    const complet = `${indicatif}${local}`;
    return complet.length >= 8 && complet.length <= 15 ? `+${complet}` : null;
}
/** Forme lisible à l'écran : « +237 6 99 00 01 11 » reste un numéro, pas un code. */
export function afficherTelephone(e164) {
    const m = /^\+(\d{1,3})(\d+)$/.exec(e164);
    if (!m)
        return e164;
    const reste = (m[2] ?? '').replace(/(\d{2})(?=\d)/g, '$1 ');
    return `+${m[1]} ${reste}`.trim();
}
/**
 * L'indicatif à pré-inscrire dans un champ de saisie : « +223 ».
 *
 * ⭐ Pré-saisi plutôt que déduit à l'enregistrement. Un champ vide invite à
 * taper le numéro comme on le dicte au comptoir — « 76 12 34 56 » — et c'est
 * l'application qui devine ensuite le pays, souvent mal dès qu'une cliente
 * n'est pas du pays de la boutique. L'indicatif visible pose la question au
 * bon moment : à la saisie, devant la personne qui connaît la réponse.
 */
export function prefixePays(pays) {
    const code = pays ? INDICATIFS[pays.toUpperCase()] : undefined;
    return code ? `+${code}` : null;
}
/**
 * La saisie porte-t-elle un indicatif international ?
 *
 * ⚠️ Un « + » seul ne suffit pas : il faut au moins un chiffre derrière, sinon
 * un champ où l'on a effacé le numéro en gardant le préfixe passerait pour
 * renseigné.
 */
export function aUnPrefixe(valeur) {
    return /^\+\d/.test(valeur.trim());
}
/**
 * Ce qu'il faut retenir d'un champ téléphone pré-rempli d'un indicatif.
 *
 * ⚠️ Un champ laissé À L'INDICATIF SEUL vaut VIDE. Sans cette règle, toute
 * fiche créée sans téléphone se retrouverait avec « +223 » en guise de numéro —
 * une valeur qui n'appelle personne, mais que tout le reste du logiciel
 * prendrait pour un numéro renseigné.
 */
export function saisieTelephone(valeur, prefixe) {
    const propre = valeur.trim();
    if (!propre)
        return null;
    if (prefixe && propre.replace(/\s/g, '') === prefixe.replace(/\s/g, ''))
        return null;
    // Un « + » orphelin, ou des espaces autour : même conclusion.
    if (/^\+\s*$/.test(propre))
        return null;
    return propre;
}
//# sourceMappingURL=e164.js.map